
import { format } from 'date-fns';
import clip from '@/utils/clipboard.js';
import Vue from 'vue';
import { interpreterForChannel } from './interpreters';
import { InterchangeEventTypes, type InterchangeEventType } from '@/schema/interchange_event';
import type { InterchangeEventRow } from './interpreters/types';

type ActionInterchangeItem = {
	interchange_by_reference?: {
		reference?: string;
		channel?: string;
		interchange_event?: Array<{
			reference: string;
			created_at?: string;
			event_type?: string;
			event_subtype?: string;
			raw_data?: unknown;
			failure?: boolean;
		}>;
	};
};

function flattenActionInterchanges(actionInterchanges: ActionInterchangeItem[]): InterchangeEventRow[] {
	const rows: InterchangeEventRow[] = [];
	(actionInterchanges || []).forEach(item => {
		const interchange = item.interchange_by_reference;
		const ev = interchange?.interchange_event;
		const events = Array.isArray(ev) ? ev : [];
		events.forEach(event => {
			rows.push({
				reference: event.reference,
				interchange_channel: interchange?.channel || '',
				timestamp: event.created_at || '',
				channel_endpoint: {},
				event_type: (event.event_type || InterchangeEventTypes.UNKNOWN) as InterchangeEventType,
				event_subtype: event.event_subtype,
				raw_data: event.raw_data ?? {},
				failure: event.failure ?? false
			});
		});
	});
	return rows;
}

export default Vue.extend({
	name: 'InterchangeEventDetail',
	props: {
		actionInterchanges: {
			type: Array,
			default: () => []
		},
		initialEventRef: { type: String, default: '' }
	},
	data() {
		return {
			selectedRow: null as InterchangeEventRow | null
		};
	},
	computed: {
		rows(): InterchangeEventRow[] {
			return flattenActionInterchanges(this.actionInterchanges as ActionInterchangeItem[]);
		},
		interpretedComponent() {
			const row = this.selectedRow;
			if (!row) {
				return null;
			}
			return interpreterForChannel(row.interchange_channel);
		}
	},
	watch: {
		rows: {
			immediate: true,
			handler() {
				this.syncSelection(this.rows);
			}
		},
		initialEventRef() {
			this.syncSelection(this.rows);
		}
	},
	methods: {
		format,
		formatCreatedAt(row: InterchangeEventRow) {
			const ts = row.timestamp;
			if (!ts) {
				return '—';
			}
			try {
				return format(new Date(ts), 'd/MM/yyyy - HH:mm');
			} catch {
				return '—';
			}
		},
		eventTypeTagType(eventType: string) {
			const map: Record<string, string> = {
				DELIVERY: 'success',
				DELIVERED: 'success',
				PROCESSING: 'warning',
				BOUNCE: 'danger',
				TRANSIT: 'info',
				OPEN: 'info',
				CLICK: 'info',
				UNKNOWN: ''
			};
			return map[eventType] ?? '';
		},
		shortInterchange(row: InterchangeEventRow) {
			const reference = row.reference;
			if (!reference || typeof reference !== 'string') {
				return '—';
			}
			return reference.substring(0, 8);
		},
		rawJson(row: InterchangeEventRow | null) {
			if (!row?.raw_data) {
				return '';
			}
			try {
				return JSON.stringify(row.raw_data, null, 2);
			} catch {
				return '';
			}
		},
		copyRawData(event: Event) {
			const text = this.rawJson(this.selectedRow);
			if (!text) {
				return;
			}
			event.stopPropagation();
			clip(text, event);
		},
		handleCurrentChange(row: InterchangeEventRow | null) {
			this.selectedRow = row;
		},
		syncSelection(rows: InterchangeEventRow[]) {
			if (!rows || rows.length === 0) {
				this.selectedRow = null;
				return;
			}
			const want = this.initialEventRef;
			const match = want ? rows.find(r => r.reference === want) : null;
			this.selectedRow = match || rows[0];
			this.$nextTick(() => {
				const table = this.$refs.eventTable as { setCurrentRow?: (r: InterchangeEventRow | null) => void } | undefined;
				if (table && typeof table.setCurrentRow === 'function') {
					table.setCurrentRow(this.selectedRow);
				}
			});
		}
	}
});
